import React, { useState, useEffect } from 'react';
import { Box, Button, Text, Code, Collapse } from '@chakra-ui/react';
import { getDiscordConfig } from '../config/discord';
import { getGoogleConfig } from '../config/google';
import { getApiBaseUrl } from '../utils/environment';

/**
 * OAuth Debug Information Component
 * 
 * This component displays debug information about the OAuth configuration
 * to help troubleshoot authentication issues. It shows redirect URIs,
 * API endpoints, and other relevant configuration.
 */
export const OAuthDebugInfo: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [discordConfig, setDiscordConfig] = useState<any>(null);
  const [googleConfig, setGoogleConfig] = useState<any>(null);

  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const [discord, google] = await Promise.all([
          getDiscordConfig(),
          getGoogleConfig()
        ]);
        setDiscordConfig(discord);
        setGoogleConfig(google);
      } catch (error) {
        console.error('Failed to load OAuth configs:', error);
      }
    };
    loadConfigs();
  }, []);

  return (
    <Box mt={4} p={3} borderRadius="md" bg="gray.800" fontSize="sm">
      <Button 
        size="xs" 
        colorScheme="blue" 
        onClick={() => setIsOpen(!isOpen)}
        mb={2}
      >
        {isOpen ? 'Hide Debug Info' : 'Show Debug Info'}
      </Button>
      
      <Collapse in={isOpen} animateOpacity>
        <Box p={2} mt={2} borderRadius="md" bg="gray.700">
          <Text fontWeight="bold" mb={2}>Discord OAuth Configuration:</Text>
          {discordConfig ? (
            <>
              <Text>
                Redirect URI: <Code>{discordConfig.redirectUri}</Code>
              </Text>
              <Text>
                API Endpoint: <Code>{discordConfig.apiEndpoint}</Code>
              </Text>
              <Text>
                Scopes: <Code>{discordConfig.scopes.join(', ')}</Code>
              </Text>
            </>
          ) : (
            <Text color="gray.400">Loading Discord configuration...</Text>
          )}
          
          <Box mt={3} mb={3} borderTop="1px solid" borderColor="gray.600" />
          
          <Text fontWeight="bold" mb={2}>Google OAuth Configuration:</Text>
          {googleConfig ? (
            <>
              <Text>
                Redirect URI: <Code>{googleConfig.redirectUri}</Code>
              </Text>
              <Text>
                API Endpoint: <Code>{googleConfig.apiEndpoint}</Code>
              </Text>
              <Text>
                Scopes: <Code>{googleConfig.scopes.join(', ')}</Code>
              </Text>
            </>
          ) : (
            <Text color="gray.400">Loading Google configuration...</Text>
          )}
          
          <Box mt={3} mb={3} borderTop="1px solid" borderColor="gray.600" />
          
          <Text fontWeight="bold" mb={2}>Environment Information:</Text>
          <Text>
            Hostname: <Code>{window.location.hostname}</Code>
          </Text>
          <Text>
            Origin: <Code>{window.location.origin}</Code>
          </Text>
          <Text>
            API Base URL: <Code>{getApiBaseUrl()}</Code>
          </Text>
          <Text>
            Is Production: <Code>{window.location.hostname !== 'localhost' ? 'true' : 'false'}</Code>
          </Text>
          
          <Box mt={3}>
            <Text fontSize="xs" color="gray.400">
              Note: This debug information is only shown in development. 
              You can use it to verify that OAuth is properly configured.
            </Text>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default OAuthDebugInfo;
